import { useEffect, useRef, useState } from "react"
import { ArrowLeft, Check, RotateCcw } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, cx } from "../../components/ui"
import { AuthLayout } from "./AuthLayout"

const LENGTH = 6

/**
 * Step two: the six-digit code from the email.
 *
 * Six separate boxes rather than one field, because that is what people expect
 * from a code and it makes a paste of the whole code work without thinking.
 */
export function CodeStep({ challenge, email, onBack, onVerified }) {
	const { config, toast } = useApp()
	const [digits, setDigits] = useState(Array(LENGTH).fill(""))
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)
	const [challengeId, setChallengeId] = useState(challenge.challenge_id)
	const [cooldown, setCooldown] = useState(0)
	const inputs = useRef([])

	useEffect(() => {
		inputs.current[0]?.focus()
	}, [])

	useEffect(() => {
		if (cooldown <= 0) return undefined
		const timer = window.setTimeout(() => setCooldown((n) => n - 1), 1000)
		return () => window.clearTimeout(timer)
	}, [cooldown])

	const code = digits.join("")

	async function verify(value = code) {
		if (value.length !== LENGTH || busy) return
		setBusy(true)
		setError(null)
		try {
			const session = await api.verify({ challenge_id: challengeId, code: value })
			await onVerified(session)
		} catch (cause) {
			setError(cause.message)
			setDigits(Array(LENGTH).fill(""))
			inputs.current[0]?.focus()
		} finally {
			setBusy(false)
		}
	}

	function setDigit(index, value) {
		const cleaned = value.replace(/\D/g, "")
		if (!cleaned) {
			setDigits((current) => current.map((d, i) => (i === index ? "" : d)))
			return
		}

		// A pasted code fills every box from wherever the caret was.
		const next = [...digits]
		cleaned.split("").forEach((char, offset) => {
			if (index + offset < LENGTH) next[index + offset] = char
		})
		setDigits(next)

		const landed = Math.min(index + cleaned.length, LENGTH - 1)
		inputs.current[landed]?.focus()

		const joined = next.join("")
		if (joined.length === LENGTH) verify(joined)
	}

	function onKeyDown(index, event) {
		if (event.key === "Backspace" && !digits[index] && index > 0) {
			inputs.current[index - 1]?.focus()
		}
		if (event.key === "ArrowLeft" && index > 0) inputs.current[index - 1]?.focus()
		if (event.key === "ArrowRight" && index < LENGTH - 1) inputs.current[index + 1]?.focus()
	}

	async function resend() {
		setBusy(true)
		try {
			const started = await api.resendCode(challengeId)
			setChallengeId(started.challenge_id)
			setDigits(Array(LENGTH).fill(""))
			setError(null)
			setCooldown(30)
			toast("A new code is on its way.", "success")
			if (started.debug_code) toast(`Local code: ${started.debug_code}`, "info")
			inputs.current[0]?.focus()
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<AuthLayout
			title="Enter your code"
			description={`We emailed a ${LENGTH}-digit code to ${email}. It expires in ${
				config?.twofa_ttl_minutes ?? 10
			} minutes.`}
		>
			<div className="space-y-4">
				<div className="flex justify-between gap-2" onPaste={(event) => {
					const text = event.clipboardData?.getData("text") || ""
					if (/\d/.test(text)) {
						event.preventDefault()
						setDigit(0, text)
					}
				}}>
					{digits.map((digit, index) => (
						<input
							key={index}
							ref={(node) => {
								inputs.current[index] = node
							}}
							value={digit}
							onChange={(event) => setDigit(index, event.target.value)}
							onKeyDown={(event) => onKeyDown(index, event)}
							inputMode="numeric"
							autoComplete={index === 0 ? "one-time-code" : "off"}
							maxLength={LENGTH}
							aria-label={`Digit ${index + 1}`}
							disabled={busy}
							className={cx(
								"h-12 w-full rounded-lg border text-center text-lg font-semibold tabular",
								"text-ink-900 transition-colors focus:outline-none",
								error
									? "border-rose-300 bg-rose-50"
									: "border-ink-200 bg-white focus:border-brand-400"
							)}
						/>
					))}
				</div>

				{error && <p className="text-[0.8125rem] text-rose-600">{error}</p>}

				{challenge.debug_code && (
					<p className="rounded-lg bg-amber-50 px-3 py-2 text-2xs leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200">
						<strong>Local development.</strong> No Resend key is configured, so the
						email was printed to the API console. This code is{" "}
						<span className="font-mono font-semibold">{challenge.debug_code}</span>.
					</p>
				)}

				<Button
					variant="primary"
					size="lg"
					className="w-full"
					icon={Check}
					busy={busy}
					disabled={busy || code.length !== LENGTH}
					onClick={() => verify()}
				>
					Verify and sign in
				</Button>

				<div className="flex items-center justify-between">
					<Button size="sm" variant="ghost" icon={ArrowLeft} onClick={onBack}>
						Back
					</Button>
					<Button
						size="sm"
						variant="ghost"
						icon={RotateCcw}
						onClick={resend}
						disabled={busy || cooldown > 0}
					>
						{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
					</Button>
				</div>
			</div>
		</AuthLayout>
	)
}
